import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StepIndicator } from './StepIndicator';
import { PersonalDetails, PersonalDetailsRef } from '../../components/steps/PersonalDetails';
import { Qualification, QualificationRef } from '../../components/steps/Qualification';
import { About, AboutRef } from '../../components/steps/About';
import { References, ReferencesRef } from '../../components/steps/References';
import { Identification, IdentificationRef } from '../../components/steps/Identification';
import { createBdeOnboarding, createUserOnboarding } from '../../services/usersapi';
import { useRbac } from '../../context'; // Import the RBAC context to get user info
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// Define the IOnboardingData interface
interface IOnboardingData {
  updatedDateTime: string;
  personalDetails: {
    fullName: string;
    email: string;
    role: string;
    phone: string;
    wtspNum: string;
    gender: string;
    dob: string;
    applyFor: string;
    currBuildingNo: string;
    currStreet: string;
    currArea: string;
    currCity: string;
    currState: string;
    currPin: string;
    permBuildingNo: string;
    permStreet: string;
    permArea: string;
    permCity: string;
    permState: string;
    permPin: string;
    languages: string[];
  };
  qualification: {
    highestQualification: string;
    workExperience: Array<{
      CompanyName: string;
      ExperienceYears: string;
    }>;
  };
  about: {
    questions: Array<{
      question: string;
      answer: string;
    }>;
  };
  familyMembers: {
    [key: string]: {
      name: string;
      age: string;
      relationship: string;
      organization: string;
      dependentOnYou: string;
    };
  };
  newIdentityProof: {
    aadharFront: string;
    aadharBack: string;
    aadharNumber: string;
    panFront: string;
    panBack: string;
    panNumber: string;
    emergencyNumber: string;
    photo: string;
  };
  references: {
    [key: string]: {
      name: string;
      designation: string;
      organization: string;
      email: string;
      phone: string;
      connection: string;
    };
  };
}

export interface FormData {
  personalDetails: {
    fullName: string;
    dateOfBirth: string;
    gender: string;
    phoneNumber: string;
    whatsappNumber: string;
    email: string;
    currentAddress: {
      buildingFlat: string;
      street: string;
      areaVillage: string;
      cityTownBlock: string;
      state: string;
      pincode: string;
    };
    permanentAddress: {
      buildingFlat: string;
      street: string;
      areaVillage: string;
      cityTownBlock: string;
      state: string;
      pincode: string;
    };
    languages: string[];
  applyFor: string;
  spokenLanguages: string[];
  };
  qualification: {
    highestQualification: string;
    workExperience: Array<{
      company: string;
      experienceYears: string;
    }>;
  };
  about: {
    tellAboutYourself: string;
    whyJoinHR: string;
    howContribute: string;
    hoursContribute: string;
    categories: string[];
    availability: string;
  };
  references: Array<{
    name: string;
    contactNumber: string;
    email: string;
    organization: string;
    designation: string;
    howTheyKnow: string;
  }>;
  identification: {
    profilePhoto?: string;
    aadharFront?: string;
    aadharBack?: string;
    panFront?: string;
    panBack?: string;
    aadharNumber: string;
    panNumber: string;
    emergencyContact: string;
    familyMembers: Array<{
      name: string;
      relationship: string;
      occupation: string;
      age: string;
      dependent: boolean;
    }>;
  };
}

const steps = [
  { id: 1, title: 'Personal Details', component: PersonalDetails },
  { id: 2, title: 'Qualification/Certification', component: Qualification },
  { id: 3, title: 'About', component: About },
  { id: 4, title: 'References', component: References },
  { id: 5, title: 'Identification', component: Identification },
];

interface MultiStepFormProps {
  onFormSubmit?: () => void;
  isCompact?: boolean; // New prop to control layout
}

const sanitizeIdentificationUrls = (data: FormData): FormData => {
  const imageFields = ['profilePhoto', 'aadharFront', 'aadharBack', 'panFront', 'panBack'] as const;
  const identification = { ...data.identification };
  let changed = false;

  for (const field of imageFields) {
    const value = identification[field];
    if (value && !/^https?:\/\//i.test(value)) {
      identification[field] = '';
      changed = true;
    }
  }

  return changed ? { ...data, identification } : data;
};

export const MultiStepForm: React.FC<MultiStepFormProps> = ({ onFormSubmit, isCompact = false }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const savedStep = localStorage.getItem('multiStepFormCurrentStep');
      if (savedStep) {
        const step = parseInt(savedStep, 10);
        if (!isNaN(step)) {
          return step;
        }
      }
    } catch (error) {
      console.error("Failed to parse current step from local storage", error);
    }
    return 1;
  });
  const personalDetailsRef = useRef<PersonalDetailsRef>(null);
  const qualificationRef = useRef<QualificationRef>(null);
  const aboutRef = useRef<AboutRef>(null);
  const referencesRef = useRef<ReferencesRef>(null);
  const identificationRef = useRef<IdentificationRef>(null);
  
  const [formData, setFormData] = useState<FormData>(() => {
    try {
      const savedData = localStorage.getItem('multiStepFormData');
      if (savedData) {
        return sanitizeIdentificationUrls(JSON.parse(savedData));
      }
    } catch (error) {
      console.error("Failed to parse form data from local storage", error);
    }
    return {
      personalDetails: {
        fullName: '',
        dateOfBirth: '',
        gender: '',
        phoneNumber: '',
        whatsappNumber: '',
        email: '',
        currentAddress: {
          buildingFlat: '',
          street: '',
          areaVillage: '',
          cityTownBlock: '',
          state: '',
          pincode: '',
        },
        permanentAddress: {
          buildingFlat: '',
          street: '',
          areaVillage: '',
          cityTownBlock: '',
          state: '',
          pincode: '',
        },
        languages: [],
        spokenLanguages: [],
        applyFor: '',
      },
      qualification: {
        highestQualification: '',
        workExperience: [],
      },
      about: {
        tellAboutYourself: '',
        whyJoinHR: '',
        howContribute: '',
        hoursContribute: '',
        categories: [],
        availability: '',
      },
      references: [
        {
          name: '',
          contactNumber: '',
          email: '',
          organization: '',
          designation: '',
          howTheyKnow: '',
        },
      ],
      identification: {
        aadharNumber: '',
        panNumber: '',
        emergencyContact: '',
        familyMembers: [],
      },
    };
  });

  useEffect(() => {
    localStorage.setItem('multiStepFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('multiStepFormCurrentStep', currentStep.toString());
  }, [currentStep]);

  const updateFormData = (stepData: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const nextStep = () => {
    // Validate current step before proceeding
    let isValid = true;
    
    if (currentStep === 1) {
      // Validate Personal Details
      if (personalDetailsRef.current) {
        isValid = personalDetailsRef.current.validateForm();
      }
    } else if (currentStep === 2) {
      // Validate Qualification step
      if (qualificationRef.current) {
        isValid = qualificationRef.current.validateForm();
      }
    } else if (currentStep === 3) {
      // Validate About step
      if (aboutRef.current) {
        isValid = aboutRef.current.validateForm();
      }
    } else if (currentStep === 4) {
      // Validate References step
      if (referencesRef.current) {
        isValid = referencesRef.current.validateForm();
      }
    }
    
    if (isValid && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else if (!isValid) {
      // Scroll to top to show validation errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Add useRbac hook
  const { user } = useRbac();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!identificationRef.current?.validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // If all validations pass, proceed with form submission
    try {
      setIsSubmitting(true);
      const loadingToast = toast.loading('Submitting your application...');

      if (!user?.id) {
        toast.dismiss(loadingToast);
        toast.error('User ID not found. Please log in again.');
        return;
      }

      // Transform form data to match onboarding data structure
      const onboardingData: IOnboardingData = {
        updatedDateTime: new Date().toISOString(),
        personalDetails: {
          fullName: formData.personalDetails.fullName,
          email: formData.personalDetails.email,
          role: user.role,
          phone: formData.personalDetails.phoneNumber,
          wtspNum: formData.personalDetails.whatsappNumber,
          gender: formData.personalDetails.gender,
          dob: formData.personalDetails.dateOfBirth,
          applyFor: "hm",
          currBuildingNo: formData.personalDetails.currentAddress.buildingFlat,
          currStreet: formData.personalDetails.currentAddress.street,
          currArea: formData.personalDetails.currentAddress.areaVillage,
          currCity: formData.personalDetails.currentAddress.cityTownBlock,
          currState: formData.personalDetails.currentAddress.state,
          currPin: formData.personalDetails.currentAddress.pincode,
          permBuildingNo: formData.personalDetails.permanentAddress.buildingFlat,
          permStreet: formData.personalDetails.permanentAddress.street,
          permArea: formData.personalDetails.permanentAddress.areaVillage,
          permCity: formData.personalDetails.permanentAddress.cityTownBlock,
          permState: formData.personalDetails.permanentAddress.state,
          permPin: formData.personalDetails.permanentAddress.pincode,
          languages: formData.personalDetails.languages,
        },
        qualification: {
          highestQualification: formData.qualification.highestQualification,
          workExperience: formData.qualification.workExperience.map(exp => ({
            CompanyName: exp.company,
            ExperienceYears: exp.experienceYears
          }))
        },
        about: {
          questions: [
            {
              question: "Tell about yourself",
              answer: formData.about.tellAboutYourself
            },
            {
              question: "Why do you want to join HR?",
              answer: formData.about.whyJoinHR
            },
            {
              question: "How will you contribute?",
              answer: formData.about.howContribute
            },
            {
              question: "How many hours can you contribute?",
              answer: formData.about.hoursContribute
            },
        
          ]
        },
        familyMembers: formData.identification.familyMembers.reduce((acc, member, index) => ({
          ...acc,
          [`member${index + 1}`]: {
            name: member.name,
            age: member.age,
            relationship: member.relationship,
            organization: member.occupation,
            dependentOnYou: member.dependent ? "yes" : "no"
          }
        }), {}),
        newIdentityProof: {
          aadharFront: formData.identification.aadharFront || '',
          aadharBack: formData.identification.aadharBack || '',
          aadharNumber: formData.identification.aadharNumber,
          panFront: formData.identification.panFront || '',
          panBack: formData.identification.panBack || '',
          panNumber: formData.identification.panNumber,
          emergencyNumber: formData.identification.emergencyContact,
          photo: formData.identification.profilePhoto || '',
        },
        references: formData.references.reduce((acc, ref, index) => ({
          ...acc,
          [`person${index + 1}`]: {
            name: ref.name,
            designation: ref.designation,
            organization: ref.organization,
            email: ref.email,
            phone: ref.contactNumber,
            connection: ref.howTheyKnow
          }
        }), {})
      };

      // Call the onboarding API
      let response = null;

      if(user.role === "MASTER_BDE" || user.role === "BDE_INTERN" || user.role === "FREELANCE_BDE") {
        response = await createBdeOnboarding(user.id, onboardingData);
      } else {
        response = await createUserOnboarding(user.id, onboardingData);
      }

      console.log("onboarding data called",response);

      // Clear local storage after successful submission
      localStorage.removeItem('multiStepFormData');
      localStorage.removeItem('multiStepFormCurrentStep');

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('Application submitted successfully!');

      // Call the onFormSubmit callback if provided
      if (onFormSubmit) {
        onFormSubmit();
      }

      // Use window.location for full page reload and navigation
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500); // Wait for 1.5 seconds to show the success message

    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepData = steps.find(step => step.id === currentStep);

  return (
    <div className={isCompact ? "w-full" : "min-h-screen bg-gray-50 py-4 px-2 sm:px-4 lg:px-8"}>
      <div className="w-full max-w-none mx-auto">
        <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />
        
        <Card className="shadow-lg border border-gray-200 w-full bg-white">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-orange-600 mb-2">
                {currentStepData?.title}
              </h2>
              {/* <p className="text-sm text-red-500 mb-4">
                <span className="text-red-500">(*)</span> Indicates required field
              </p> */}
            </div>

            <div className="w-full">
              {currentStep === 1 ? (
                <PersonalDetails
                  ref={personalDetailsRef}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              ) : currentStep === 2 ? (
                <Qualification
                  ref={qualificationRef}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              ) : currentStep === 3 ? (
                <About
                  ref={aboutRef}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              ) : currentStep === 4 ? (
                <References
                  ref={referencesRef}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              ) : currentStep === 5 ? (
                <Identification
                  ref={identificationRef}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              ) : null}
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-2 w-full sm:w-auto order-2 sm:order-1"
              >
                Back
              </Button>

              {currentStep === steps.length ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto order-1 sm:order-2 relative"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto order-1 sm:order-2"
                >
                  Save & Next
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};